import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

export default function Top10Page() {
  const [boyCharacters, setBoyCharacters] = useState<AnimeCharacter[]>([]);
  const [girlCharacters, setGirlCharacters] = useState<AnimeCharacter[]>([]);
  const [randomCharacters, setRandomCharacters] = useState<AnimeCharacter[]>([]);
  const [featuredCharacter, setFeaturedCharacter] = useState<AnimeCharacter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    loadTopCharacters();
    loadRandomCharacters();
    loadFeaturedCharacter();
  }, []);

  const loadTopCharacters = async () => {
    try {
      // Set a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      // Load data with timeout - using pagination to ensure we get true top 10
      const loadData = async () => {
        // Load ALL boy characters using pagination
        let allBoys: AnimeCharacter[] = [];
        let page = 0;
        const pageSize = 1000;
        let hasMoreBoys = true;

        while (hasMoreBoys) {
          const { data, error } = await supabase
            .from('anime_characters')
            .select('*')
            .eq('gender', 'boy')
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (error) throw new Error(`Failed to load boy characters: ${error.message}`);

          if (data && data.length > 0) {
            allBoys = [...allBoys, ...data];
            page++;
            
            if (data.length < pageSize) {
              hasMoreBoys = false;
            }
          } else {
            hasMoreBoys = false;
          }
        }

        // Load ALL girl characters using pagination
        let allGirls: AnimeCharacter[] = [];
        page = 0;
        let hasMoreGirls = true;

        while (hasMoreGirls) {
          const { data, error } = await supabase
            .from('anime_characters')
            .select('*')
            .eq('gender', 'girl')
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (error) throw new Error(`Failed to load girl characters: ${error.message}`);

          if (data && data.length > 0) {
            allGirls = [...allGirls, ...data];
            page++;
            
            if (data.length < pageSize) {
              hasMoreGirls = false;
            }
          } else {
            hasMoreGirls = false;
          }
        }

        // Sort and get top 10 for each gender
        const topBoys = allBoys
          .sort((a, b) => {
            // First sort by average_rating (descending)
            if (b.average_rating !== a.average_rating) {
              return b.average_rating - a.average_rating;
            }
            // If ratings are equal, sort by total_ratings (descending)
            return b.total_ratings - a.total_ratings;
          })
          .slice(0, 10);

        const topGirls = allGirls
          .sort((a, b) => {
            // First sort by average_rating (descending)
            if (b.average_rating !== a.average_rating) {
              return b.average_rating - a.average_rating;
            }
            // If ratings are equal, sort by total_ratings (descending)
            return b.total_ratings - a.total_ratings;
          })
          .slice(0, 10);

        return { boysResult: topBoys, girlsResult: topGirls };
      };

      const { boysResult, girlsResult } = await Promise.race([
        loadData(),
        timeoutPromise
      ]) as { boysResult: AnimeCharacter[]; girlsResult: AnimeCharacter[] };

      setBoyCharacters(boysResult || []);
      setGirlCharacters(girlsResult || []);
      
    } catch (err) {
      console.error('Error loading top characters:', err);
      setError(err instanceof Error ? err.message : 'Failed to load characters');
    } finally {
      setLoading(false);
    }
  };

  const loadRandomCharacters = async () => {
    try {
      // Get total count of characters
      const { count } = await supabase
        .from('anime_characters')
        .select('*', { count: 'exact', head: true });

      if (!count || count === 0) return;

      // Generate 6 random offsets
      const randomOffsets = Array.from({ length: 6 }, () => 
        Math.floor(Math.random() * count)
      );

      // Fetch characters at random positions
      const characterPromises = randomOffsets.map(offset =>
        supabase
          .from('anime_characters')
          .select('*')
          .range(offset, offset)
          .single()
      );

      const results = await Promise.all(characterPromises);
      const characters = results
        .filter(result => result.data)
        .map(result => result.data);

      setRandomCharacters(characters);
    } catch (err) {
      console.error('Error loading random characters:', err);
    }
  };

  const loadFeaturedCharacter = async () => {
    try {
      // Get total count of characters with descriptions
      const { count } = await supabase
        .from('anime_characters')
        .select('*', { count: 'exact', head: true })
        .not('description', 'is', null)
        .neq('description', '');

      if (!count || count === 0) return;

      // Generate a random offset
      const randomOffset = Math.floor(Math.random() * count);

      // Fetch character at random position
      const { data } = await supabase
        .from('anime_characters')
        .select('*')
        .not('description', 'is', null)
        .neq('description', '')
        .range(randomOffset, randomOffset)
        .single();

      if (data) {
        setFeaturedCharacter(data);
      }
    } catch (err) {
      console.error('Error loading featured character:', err);
    }
  };

  const retryLoad = () => {
    setError(null);
    setLoading(true);
    loadTopCharacters();
  };

  // Helper function to create SEO-friendly slug
  const createCharacterSlug = (character: AnimeCharacter) => {
    const animeName = character.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const charName = character.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animeName}-${charName}-${character.id}`;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Top 10 Hottest Anime Characters',
    description: 'The highest rated anime characters based on community votes',
    numberOfItems: girlCharacters.length + boyCharacters.length,
    itemListElement: [
      ...girlCharacters.map((character, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Person',
          name: character.character_name,
          image: character.image_url,
          url: `${siteUrl}/character/${createCharacterSlug(character)}`,
          gender: 'Female',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: character.average_rating.toFixed(2),
            bestRating: '10',
            worstRating: '1',
            ratingCount: character.total_ratings
          }
        }
      })),
      ...boyCharacters.map((character, index) => ({
        '@type': 'ListItem',
        position: girlCharacters.length + index + 1,
        item: {
          '@type': 'Person',
          name: character.character_name,
          image: character.image_url,
          url: `${siteUrl}/character/${createCharacterSlug(character)}`,
          gender: 'Male',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: character.average_rating.toFixed(2),
            bestRating: '10',
            worstRating: '1',
            ratingCount: character.total_ratings
          }
        }
      }))
    ]
  };

  const renderCharacterList = (characters: AnimeCharacter[], startIndex: number = 0) => (
    <div className="space-y-3 md:space-y-4">
      {characters.map((character, index) => (
        <Link
          key={character.id}
          to={`/character/${createCharacterSlug(character)}`}
          className="block bg-white rounded-xl md:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
        >
          <div className="flex items-center gap-3 md:gap-6 p-3 md:p-6">
            <div className="flex-shrink-0">
              <div
                className={`w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full text-lg md:text-2xl font-bold ${
                  index === 0
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white'
                    : index === 1
                    ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white'
                    : index === 2
                    ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
                    : 'bg-gradient-to-r from-pink-100 to-purple-100 text-gray-700'
                }`}
              >
                {startIndex + index + 1}
              </div>
            </div>

            <div className="w-16 h-16 md:w-24 md:h-24 flex-shrink-0">
              <img
                src={character.image_url}
                alt={character.character_name}
                className="w-full h-full object-cover object-top rounded-lg"
              />
            </div>

            <div className="flex-1 min-w-0 pr-2">
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 truncate">
                {character.character_name}
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 truncate">
                from {character.anime_name}
              </p>
              <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <i className="ri-heart-fill text-pink-500"></i>
                  <span className="font-semibold">
                    {character.average_rating.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-user-line text-gray-500"></i>
                  <span className="hidden sm:inline">{character.total_ratings} ratings</span>
                  <span className="sm:hidden">{character.total_ratings}</span>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 hidden sm:block">
              <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="Top 10 Hottest Anime Characters - Community Rankings"
        description="Discover the top 10 hottest anime characters based on community ratings. See the highest rated male and female anime characters voted by fans worldwide."
        keywords="top anime characters, hottest anime characters, best anime characters, top 10 anime, anime character rankings, popular anime characters"
        canonical={`${siteUrl}/top10`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Top 10 Hottest Characters
            </h1>
            <p className="text-sm text-gray-600">
              The highest rated anime characters based on community votes
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent mb-4"></div>
              <p className="text-gray-600">Loading top characters...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-xl mx-2">
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-error-warning-line text-4xl text-red-500"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={retryLoad}
                className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-8 md:space-y-12">
                {/* Top 10 Girls */}
                <div>
                  <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 px-2">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg">
                      <i className="ri-women-line text-lg md:text-xl text-white"></i>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      Top 10 Girls
                    </h2>
                  </div>
                  {girlCharacters.length > 0 ? (
                    renderCharacterList(girlCharacters)
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl md:rounded-2xl mx-2">
                      <p className="text-gray-500 text-sm md:text-base">No girl characters rated yet</p>
                    </div>
                  )}
                </div>

                {/* Top 10 Boys */}
                <div>
                  <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 px-2">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg">
                      <i className="ri-men-line text-lg md:text-xl text-white"></i>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      Top 10 Boys
                    </h2>
                  </div>
                  {boyCharacters.length > 0 ? (
                    renderCharacterList(boyCharacters)
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl md:rounded-2xl mx-2">
                      <p className="text-gray-500 text-sm md:text-base">No boy characters rated yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* More Characters Section */}
              {randomCharacters.length > 0 && (
                <div className="bg-white rounded-2xl shadow-xl p-6 mt-8 md:mt-12">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">More Characters</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                    {randomCharacters.map((char) => (
                      <Link
                        key={char.id}
                        to={`/character/${createCharacterSlug(char)}`}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-gray-100">
                          <img
                            src={char.image_url}
                            alt={char.character_name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h3 className="text-xs font-semibold text-gray-900 line-clamp-2 group-hover:text-pink-500 transition-colors">
                          {char.character_name}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-heart-fill text-xs text-pink-500"></i>
                          <span className="text-xs text-gray-600">{char.average_rating.toFixed(1)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Featured Character Section */}
              {featuredCharacter && (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-8 md:mt-12">
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Featured Character</h2>
                  </div>
                  <Link
                    to={`/character/${createCharacterSlug(featuredCharacter)}`}
                    className="block hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row gap-6 p-6 pt-0">
                      <div className="flex-shrink-0 w-full sm:w-48">
                        <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={featuredCharacter.image_url}
                            alt={featuredCharacter.character_name}
                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {featuredCharacter.character_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          From <span className="font-semibold">{featuredCharacter.anime_name}</span>
                        </p>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="flex items-center gap-1">
                            <i className="ri-heart-fill text-pink-500"></i>
                            <span className="text-sm font-semibold text-gray-900">
                              {featuredCharacter.average_rating.toFixed(1)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {featuredCharacter.total_ratings} ratings
                          </div>
                        </div>
                        {featuredCharacter.description && (
                          <div 
                            className="text-sm text-gray-700 leading-relaxed character-description-content"
                            dangerouslySetInnerHTML={{ __html: featuredCharacter.description }}
                          />
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
      <style>{`
        .character-description-content p {
          margin-bottom: 1.5em;
        }
        .character-description-content p:last-child {
          margin-bottom: 0;
        }
        .character-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .character-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
